import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Typography, useMediaQuery, useTheme } from '@mui/material';
import moment from 'moment';
import { addDays, format, startOfWeek, subDays } from 'date-fns';

import { FBoxContainer, FHButton, FHSideDrawer } from '../../../components/base';
import ClientCard from './ClientCard';
import ClientCardLoader from './CardLoader';
import SixWeekDocumentUtilizationSummary from '../../../components/layout/dashboard/utilization/SixWeekDocumentUtilizationSummary';
import ClientInsightDetails from '../../../components/layout/dashboard/ClientInsightDetails';
import {
  clientActions,
  clientSupervisionActions,
  parentSupervisionActions,
  providerAssignmentActions,
  treatmentPlanActions,
  utilizationActions,
} from '../../../store';
import { areUserRolesIn } from '../../../helpers';
import {
  mergeClientParentSessionUtilization,
  processClientParentSNUtilizations,
} from '../../../components/layout/dashboard/utils';
import { ClinicalRelationships, Roles } from '../../../constants';
import { ClinicalStatus } from '../../../admin/constants/general';
import { activeButtonStyles, AssignmentType, buttonStyles } from '../constants';
import { FORTA_BLUE, FORTA_WHITE } from '../../../utils/colors';
import { getUtcOffset } from '../../../utils/common';

import type { IOption, IProviderAssignment } from '../../../utils/types';
import type { RootState } from '../../../store';
import type {
  IClientParentSNUtilization,
  WeeklyUtilization,
} from '../../../components/layout/dashboard/ClientInsights';

function AssignedClients() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const [clientDetailsDrawer, setClientDetailsDrawer] = useState<boolean>(false);
  const [selectedClient, setSelectedClient] = useState<IOption | null>(null);
  const [sixWeeksUtilizationData, setSixWeeksUtilizationData] = useState<WeeklyUtilization[]>([]);
  const [clientParentUtilizationLoading, setClientParentUtilizationLoading] =
    useState<boolean>(false);
  const [clientParentSNUtilization, setClientParentSNUtilization] =
    useState<IClientParentSNUtilization | null>(null);

  const { provider } = useSelector((state: any) => state.providers);
  const { user } = useSelector((state: RootState) => state.auth);

  const { selected: client } = useSelector((state: RootState) => state.clients);

  const { list: providerAssignments = [], loading: providerAssignmentLoading } = useSelector(
    (state: RootState) => state.providerAssignment,
  );
  const { current: treatmentPlanInfo, currentTpLoading: tpInfoLoading } = useSelector(
    (state: RootState) => state.treatmentPlans,
  );

  const { data: sessionUtilization = [], loading: sessionUtilizationLoading } = useSelector(
    (state: RootState) => state.utilizations,
  );

  const [activeButtonIndex, setActiveButtonIndex] = useState<number>(0);
  const [selectedType, setSelectedType] = useState<AssignmentType>(AssignmentType.ALL);

  const filteredAssignments = useMemo(() => {
    const filtered = providerAssignments.filter(
      (assignment) =>
        assignment.client?.clinical_status === ClinicalStatus.CLINICAL ||
        assignment.client?.clinical_status === ClinicalStatus.PRE_CLINICAL,
    );

    if (selectedType === AssignmentType.HOME) {
      return filtered.filter((p) => p.clinical_relationship === ClinicalRelationships.HomeBcba);
    } else if (selectedType === AssignmentType.BOBBING) {
      return filtered.filter((p) => p.clinical_relationship === ClinicalRelationships.BobbingBcba);
    } else if (selectedType === AssignmentType.SUBSTITUTE) {
      return filtered.filter(
        (assigmment) => assigmment.clinical_relationship === ClinicalRelationships.SubstituteBCBA,
      );
    } else {
      return filtered;
    }
  }, [providerAssignments, selectedType]);

  useEffect(() => {
    if (!clientParentSNUtilization || !sessionUtilization) return;
    const result = mergeClientParentSessionUtilization(
      clientParentSNUtilization,
      sessionUtilization,
    );
    setSixWeeksUtilizationData(result.reverse());
  }, [clientParentSNUtilization, sessionUtilization]);

  useEffect(() => {
    if (!areUserRolesIn(user?.roles, [Roles.Bcba]) || !provider?.id) return;
    dispatch(
      providerAssignmentActions.getList({
        getAccessTokenSilently,
        active_only: true,
        provider_id: provider.id,
        clinical_relationship: [
          ClinicalRelationships.HomeBcba,
          ClinicalRelationships.BobbingBcba,
          ClinicalRelationships.SubstituteBCBA,
        ],
      }),
    );
  }, [provider?.id]);

  useEffect(() => {
    if (selectedClient) {
      const clientId = Number(selectedClient.id);
      if (Number.isNaN(clientId)) return;
      dispatch(clientActions.getClient({ clientId, getAccessTokenSilently }));

      const startDate = format(
        subDays(startOfWeek(new Date(), { weekStartsOn: 6 }), 7 * 5),
        'yyyy-MM-dd',
      );
      const endDate = format(
        addDays(startOfWeek(new Date(), { weekStartsOn: 6 }), 6),
        'yyyy-MM-dd',
      );
      const utcOffset = getUtcOffset(moment());

      dispatch(
        utilizationActions.getUtilization({
          getAccessTokenSilently,
          clientIds: clientId,
          startDate,
          endDate,
          utcOffset,
        }),
      );
      dispatch(
        clientActions.getClientsFamily({
          getAccessTokenSilently,
          dateActive: moment().toISOString(),
          client_id: clientId,
        }),
      );
      fetchSixWeeksClientParentUtilization(
        clientId,
        format(startOfWeek(new Date(), { weekStartsOn: 6 }), 'yyyy-MM-dd'),
        endDate,
      );
    }

    return () => {
      dispatch(clientActions.clearSelectedClient());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedClient]);

  useEffect(() => {
    if (!client?.active_treatment_plan_id) return;
    dispatch(
      treatmentPlanActions.getTreatmentPlanById({
        treatmentPlanId: client.active_treatment_plan_id,
        expand: ['client_and_family_background', 'medical_history'],
        getAccessTokenSilently,
      }),
    );

    return () => {
      dispatch(treatmentPlanActions.clearTreatmentPlan());
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [client]);

  const fetchSixWeeksClientParentUtilization = async (
    clientId: number,
    startOfWeek: string,
    endOfWeek: string,
  ) => {
    const dateArray: string[][] = [[startOfWeek, endOfWeek]];
    for (let i = 1; i < 6; i++) {
      const start = moment(startOfWeek)
        .subtract(7 * i, 'days')
        .format('YYYY-MM-DD');
      const end = moment(endOfWeek)
        .subtract(7 * i, 'days')
        .format('YYYY-MM-DD');
      dateArray.push([start, end]);
    }
    const parentSNPromises = dateArray.map((dates) => {
      return dispatch(
        parentSupervisionActions.getAggregations({
          getAccessTokenSilently,
          clientId,
          start: dates[0],
          end: dates[1],
        }),
      );
    });

    const clientSNPromises = dateArray.map((dates) => {
      return dispatch(
        clientSupervisionActions.getAggregations({
          getAccessTokenSilently,
          clientId,
          start: dates[0],
          end: dates[1],
        }),
      );
    });

    setClientParentUtilizationLoading(true);
    const parentUtilization = await Promise.all(parentSNPromises);
    const clientUtilization = await Promise.all(clientSNPromises);
    setClientParentUtilizationLoading(false);

    const results = processClientParentSNUtilizations(
      clientUtilization.map((it) => it.payload),
      parentUtilization.map((it) => it.payload),
      dateArray,
    );
    setClientParentSNUtilization(results);
  };

  const handleAssignmentTypeSelect = (type: AssignmentType, index: number) => {
    setSelectedType(type);
    setActiveButtonIndex(index);
  };

  const handleClientCardClick = (assignment: IProviderAssignment) => {
    const client = {
      id: assignment.client.id,
      label: `${assignment.client.first_name} ${assignment.client.last_name}`,
    };
    setClientDetailsDrawer(true);
    setSelectedClient(client);
    setClientParentSNUtilization(null);
  };

  return (
    <>
      <FBoxContainer sx={{ flexGrow: 0, minHeight: '450px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography sx={{ color: FORTA_BLUE.primary, fontSize: 21, fontWeight: 500 }}>
            My Clients
          </Typography>
          <Box>
            <FHButton
              sx={{ mr: 1, ...(activeButtonIndex === 0 ? activeButtonStyles : buttonStyles) }}
              textTransform="none"
              onClick={() => handleAssignmentTypeSelect(AssignmentType.ALL, 0)}
            >
              All Assignments
            </FHButton>
            <FHButton
              sx={{ mr: 1, ...(activeButtonIndex === 1 ? activeButtonStyles : buttonStyles) }}
              textTransform="none"
              onClick={() => handleAssignmentTypeSelect(AssignmentType.HOME, 1)}
            >
              Home
            </FHButton>
            <FHButton
              sx={{ mr: 1, ...(activeButtonIndex === 2 ? activeButtonStyles : buttonStyles) }}
              textTransform="none"
              onClick={() => handleAssignmentTypeSelect(AssignmentType.BOBBING, 2)}
            >
              Bobbing
            </FHButton>
            <FHButton
              sx={{ ...(activeButtonIndex === 3 ? activeButtonStyles : buttonStyles) }}
              textTransform="none"
              onClick={() => handleAssignmentTypeSelect(AssignmentType.SUBSTITUTE, 3)}
            >
              Substitute
            </FHButton>
          </Box>
        </Box>
        <Grid container spacing={3} mt={0.3}>
          {providerAssignmentLoading ? (
            Array.from({ length: 8 }).map((_, i) => (
              <Grid item key={i} xs={12} sm={6} md={4} lg={4}>
                <ClientCardLoader />
              </Grid>
            ))
          ) : filteredAssignments.length > 0 ? (
            filteredAssignments.map((assignment) => (
              <Grid item key={assignment.id} xs={12} sm={6} md={4} lg={4}>
                <ClientCard assigmment={assignment} onClick={handleClientCardClick} />
              </Grid>
            ))
          ) : (
            <Box sx={{ flexGrow: 1, p: 5 }}>
              <Typography textAlign="center">No results</Typography>
            </Box>
          )}
        </Grid>
      </FBoxContainer>
      <FHSideDrawer
        sx={{ px: isMobile ? 1 : 6, py: 4 }}
        title={selectedClient?.label ?? ''}
        titleStyles={{ fontSize: 32, pt: 0 }}
        open={clientDetailsDrawer}
        onClose={() => {
          setClientDetailsDrawer(false);
        }}
      >
        <Typography color={FORTA_BLUE.primary} fontSize={24} my={2} width={isMobile ? 320 : 720}>
          Client utilisation
        </Typography>
        <SixWeekDocumentUtilizationSummary
          sx={{ borderRadius: '0px', width: isMobile ? `calc(window.innerWidth - 10)` : 720 }}
          selectedClient={selectedClient}
          data={sixWeeksUtilizationData}
          clientParentUtilLoading={clientParentUtilizationLoading}
          sessionUtilLoading={sessionUtilizationLoading}
        />

        <Box mt={5} mb={0}>
          <Typography color={FORTA_BLUE.primary} fontSize={24}>
            Client information - based on active Treatment plan
          </Typography>
          <Box sx={{ borderRadius: '10px', height: '100%' }}>
            <ClientInsightDetails
              filterAssignmentsByClientId
              tpInfoLoading={tpInfoLoading}
              treatmentPlanInfo={treatmentPlanInfo}
              selectedClient={selectedClient}
              containerStyles={{ px: 0 }}
              cardStyles={{
                backgroundColor: FORTA_WHITE,
                border: 'none',
                boxShadow: 'none',
              }}
            />
          </Box>
        </Box>
      </FHSideDrawer>
    </>
  );
}

export default AssignedClients;
